import type { ProjectConfig } from '../types';
import type { Archetype, ArchetypeTemplate, Industry } from '../types/design-spec';
import { landingArchetype, cmsArchetype } from '../data/archetypes';
import {
  layer1Template as marvelandLayer1,
  layer2Template as marvelandLayer2,
  layer3Template as marvelandLayer3,
  layer4Template as marvelandLayer4,
} from '../data/projects/marveland';
import {
  layer1Template as dikoLayer1,
  layer2Template as dikoLayer2,
  layer3Template as dikoLayer3,
  layer4Template as dikoLayer4,
} from '../data/projects/diko';
import {
  layer1Template as doudouLayer1,
  layer2Template as doudouLayer2,
  layer3Template as doudouLayer3,
  layer4Template as doudouLayer4,
} from '../data/projects/doudou';

const archetypeMap: Record<Archetype, ArchetypeTemplate> = {
  landing: landingArchetype,
  cms: cmsArchetype,
};

export const VALID_ARCHETYPES: Archetype[] = Object.keys(archetypeMap) as Archetype[];

export function isValidArchetype(value: string | undefined): value is Archetype {
  return typeof value === 'string' && value in archetypeMap;
}

export function getArchetypeTemplate(archetype: Archetype): ArchetypeTemplate {
  const template = archetypeMap[archetype];
  if (!template) {
    throw new Error(`Unknown archetype: ${archetype}`);
  }
  return template;
}

export function getLayerTemplatesForArchetype(archetype: Archetype): [string, string, string, string] | undefined {
  return archetypeMap[archetype]?.layerTemplates;
}

export interface ProjectTemplate {
  id: string;
  name: string;
  industry: Industry;
  archetype: Archetype;
  layerTemplates: [string, string, string, string];
}

export const PROJECT_TEMPLATES: ProjectTemplate[] = [
  {
    id: 'marveland',
    name: 'Marveland 物業代理',
    industry: 'property',
    archetype: 'cms',
    layerTemplates: [marvelandLayer1, marvelandLayer2, marvelandLayer3, marvelandLayer4],
  },
  {
    id: 'diko',
    name: 'DikoCare 醫療診所',
    industry: 'medical',
    archetype: 'cms',
    layerTemplates: [dikoLayer1, dikoLayer2, dikoLayer3, dikoLayer4],
  },
  {
    id: 'doudou',
    name: 'Doudou 創意工作室',
    industry: 'creative',
    archetype: 'landing',
    layerTemplates: [doudouLayer1, doudouLayer2, doudouLayer3, doudouLayer4],
  },
];

export function getProjectTemplate(id: string): ProjectTemplate | undefined {
  return PROJECT_TEMPLATES.find((t) => t.id === id);
}

export function getLayerTemplatesForConfig(config: ProjectConfig): [string, string, string, string] | undefined {
  if (config.templateId) {
    const projectTemplate = getProjectTemplate(config.templateId);
    if (projectTemplate) return projectTemplate.layerTemplates;
  }
  return getLayerTemplatesForArchetype(config.archetype);
}

export function getTemplateSourceLabel(config: ProjectConfig): string {
  if (config.templateId) {
    const projectTemplate = getProjectTemplate(config.templateId);
    if (projectTemplate) return `project: ${projectTemplate.name}`;
  }
  if (isValidArchetype(config.archetype)) {
    return `archetype: ${config.archetype}`;
  }
  return 'archetype: fallback (marveland)';
}

export interface AssembleOptions {
  layer: 1 | 2 | 3 | 4;
  projectConfig: ProjectConfig;
  template?: string;
  modules: Map<string, string>;
  archetype?: Archetype;
}

export interface AssembleResult {
  content: string;
  unreplacedVariables: string[];
  moduleCount: number;
  variableCount: number;
}

function embedModules(content: string, modules: Map<string, string>): { content: string; count: number } {
  const moduleRegex = /<!--\s*MODULE:\s*(\S+)\s*-->/g;
  let count = 0;
  let result = content;
  let hasMatch: boolean;

  do {
    hasMatch = false;
    result = result.replace(moduleRegex, (_, moduleId: string) => {
      const moduleContent = modules.get(moduleId);
      if (!moduleContent) {
        console.warn(`Module not found: ${moduleId}`);
        return `<!-- MISSING MODULE: ${moduleId} -->`;
      }
      hasMatch = true;
      count++;
      return moduleContent;
    });
  } while (hasMatch);

  return { content: result, count };
}

function replaceVariables(content: string, variables: Record<string, string>): { content: string; count: number } {
  const variableRegex = /\{\{([A-Z_][A-Z0-9_]*)\}\}/g;
  let count = 0;
  const result = content.replace(variableRegex, (match, key: string) => {
    const value = variables[key];
    if (value === undefined || value === null) return match;
    count++;
    return value;
  });
  return { content: result, count };
}

function findUnreplacedVariables(content: string): string[] {
  const variableRegex = /\{\{([A-Z_][A-Z0-9_]*)\}\}/g;
  const matches = content.match(variableRegex) || [];
  return Array.from(new Set(matches));
}

function generateDefaultCMSData(config: ProjectConfig): string {
  const cmsData = {
    siteConfig: {
      name: config.brand.nameCn,
      nameEnglish: config.brand.nameEn,
      tagline: config.brand.tagline,
      description: config.brand.description,
      logo: config.brand.logoUrl || '',
      favicon: config.brand.faviconUrl || '',
      social: {
        facebook: config.social.facebook || '',
        instagram: config.social.instagram || '',
        whatsapp: config.social.whatsapp || (config.contact.whatsapp[0] ? `https://wa.me/${config.contact.whatsapp[0].replace(/\D/g, '')}` : ''),
        wechat: config.social.wechat || '',
      },
    },
    nav: [
      { id: 'home', label: '首頁', href: '/' },
      ...(config.pages.about ? [{ id: 'about', label: '關於我們', href: '/about' }] : []),
      ...(config.pages.services ? [{ id: 'services', label: '服務', href: '/services' }] : []),
      ...(config.pages.properties ? [{ id: 'properties', label: '物業', href: '/properties' }] : []),
      ...(config.pages.contact ? [{ id: 'contact', label: '聯絡我們', href: '/contact' }] : []),
    ],
    hero: {
      backgroundImage: config.hero.backgroundValue || '',
      tagline: config.hero.tagline,
      titleLine1: config.hero.titleLine1,
      titleLine2: config.hero.titleLine2,
      description: config.hero.description,
      primaryButtonText: config.hero.primaryButtonText,
      primaryButtonLink: config.hero.primaryButtonLink,
      secondaryButtonText: config.hero.secondaryButtonText,
      secondaryButtonLink: config.hero.secondaryButtonLink,
    },
    about: {
      enabled: config.pages.about,
      sectionTagline: '關於我們',
      sectionTitle: config.brand.nameCn,
      companyName: config.brand.nameCn,
      companyNameEnglish: config.brand.nameEn,
      establishedYear: config.brand.foundedYear,
      description: config.brand.description,
      businessScope: ['市場分析', '投資建議', '銀行估價', '市場成交及按揭轉介'],
      image: config.brand.logoUrl || '',
    },
    services: {
      enabled: config.pages.services,
      sectionTagline: '我們的服務',
      sectionTitle: '專業服務',
      sectionDescription: config.brand.tagline,
      items: config.services,
    },
    properties: config.properties ?? [],
    agents: config.team,
    news: config.news ?? [],
    contact: {
      enabled: config.pages.contact,
      sectionTagline: '聯絡我們',
      sectionTitle: '與我們聯繫',
      sectionDescription: config.brand.tagline,
      info: {
        phone: config.contact.phone,
        phoneDisplay: config.contact.phoneDisplay,
        whatsapp: config.contact.whatsapp,
        email: config.contact.email,
        address: config.contact.address,
        addressDisplay: config.contact.addressDisplay,
        businessHours: config.contact.businessHours,
        mapLink: config.contact.mapLink || '',
      },
    },
    ownerForm: {
      enabled: true,
      title: '業主放盤',
      description: '歡迎業主委托我們代理租售物業',
      fields: [
        { id: 'name', label: '姓名', type: 'text', placeholder: '您的姓名', required: true },
        { id: 'phone', label: '電話', type: 'tel', placeholder: '您的聯絡電話', required: true },
        { id: 'propertyType', label: '物業類型', type: 'select', required: true, options: [{ label: '工商廈', value: 'office' }, { label: '地舖', value: 'shop' }] },
        { id: 'message', label: '留言', type: 'textarea', placeholder: '請描述您的物業', required: false },
      ],
      whatsappNotificationNumber: config.contact.whatsapp[0] || '',
    },
    footer: {
      companyName: config.brand.nameCn,
      companyNameEnglish: config.brand.nameEn,
      description: config.brand.description,
      copyright: `© ${new Date().getFullYear()} ${config.brand.nameCn}. All rights reserved.`,
      licenseNumber: config.brand.licenseNumber || '',
      links: [
        { label: '首頁', href: '/' },
        ...(config.pages.about ? [{ label: '關於我們', href: '/about' }] : []),
        ...(config.pages.services ? [{ label: '服務', href: '/services' }] : []),
        ...(config.pages.properties ? [{ label: '物業', href: '/properties' }] : []),
        ...(config.pages.contact ? [{ label: '聯絡我們', href: '/contact' }] : []),
      ],
    },
    lastUpdated: new Date().toISOString(),
  };

  return JSON.stringify(cmsData, null, 2);
}

export function flattenConfig(config: ProjectConfig): Record<string, string> {
  const flat: Record<string, string> = {
    PROJECT_NAME: config.name,
    DISPLAY_NAME: config.displayName,
    PROJECT_TYPE: config.projectType,
    LANGUAGE: config.language,
    VERSION: config.version,

    BRAND_NAME_CN: config.brand.nameCn,
    BRAND_NAME_EN: config.brand.nameEn,
    BRAND_TAGLINE: config.brand.tagline,
    BRAND_DESCRIPTION: config.brand.description,
    BRAND_LOGO_URL: config.brand.logoUrl || '',
    BRAND_FAVICON_URL: config.brand.faviconUrl || '',
    BRAND_FOUNDED_YEAR: config.brand.foundedYear,
    BRAND_LICENSE_NUMBER: config.brand.licenseNumber || '',

    CONTACT_PHONE: config.contact.phone,
    CONTACT_PHONE_DISPLAY: config.contact.phoneDisplay,
    CONTACT_EMAIL: config.contact.email,
    CONTACT_ADDRESS: config.contact.address,
    CONTACT_ADDRESS_DISPLAY: config.contact.addressDisplay,
    CONTACT_BUSINESS_HOURS: config.contact.businessHours,
    CONTACT_MAP_LINK: config.contact.mapLink || '',
    CONTACT_WHATSAPP_LIST: config.contact.whatsapp.join(', '),
    CONTACT_WHATSAPP_FIRST: config.contact.whatsapp[0] || '',

    SOCIAL_FACEBOOK: config.social.facebook || '',
    SOCIAL_INSTAGRAM: config.social.instagram || '',
    SOCIAL_WHATSAPP: config.social.whatsapp || '',
    SOCIAL_WECHAT: config.social.wechat || '',
    SOCIAL_LINKEDIN: config.social.linkedin || '',
    SOCIAL_YOUTUBE: config.social.youtube || '',

    DESIGN_PRIMARY_COLOR: config.design.primaryColor,
    DESIGN_SECONDARY_COLOR: config.design.secondaryColor,
    DESIGN_ACCENT_COLOR: config.design.accentColor,
    DESIGN_BACKGROUND_COLOR: config.design.backgroundColor,
    DESIGN_TEXT_COLOR: config.design.textColor,
    DESIGN_DARK_MODE: config.design.darkMode ? 'true' : 'false',
    DESIGN_FONT_FAMILY: config.design.fontFamily,
    DESIGN_BORDER_RADIUS: String(config.design.borderRadius),
    DESIGN_SHADOW_INTENSITY: config.design.shadowIntensity,

    PAGE_HOME: config.pages.home ? 'true' : 'false',
    PAGE_ABOUT: config.pages.about ? 'true' : 'false',
    PAGE_SERVICES: config.pages.services ? 'true' : 'false',
    PAGE_PROPERTIES: config.pages.properties ? 'true' : 'false',
    PAGE_CONTACT: config.pages.contact ? 'true' : 'false',
    PAGE_BLOG: config.pages.blog ? 'true' : 'false',
    PAGE_FAQ: config.pages.faq ? 'true' : 'false',
    PAGE_ADMIN: config.pages.admin ? 'true' : 'false',

    HERO_TAGLINE: config.hero.tagline,
    HERO_TITLE_LINE1: config.hero.titleLine1,
    HERO_TITLE_LINE2: config.hero.titleLine2,
    HERO_DESCRIPTION: config.hero.description,
    HERO_PRIMARY_BUTTON_TEXT: config.hero.primaryButtonText,
    HERO_PRIMARY_BUTTON_LINK: config.hero.primaryButtonLink,
    HERO_SECONDARY_BUTTON_TEXT: config.hero.secondaryButtonText,
    HERO_SECONDARY_BUTTON_LINK: config.hero.secondaryButtonLink,
    HERO_BACKGROUND_TYPE: config.hero.backgroundType,
    HERO_BACKGROUND_VALUE: config.hero.backgroundValue || '',

    SERVICES_COUNT: String(config.services.length),
    SERVICES_JSON: JSON.stringify(config.services, null, 2),

    TEAM_COUNT: String(config.team.length),
    TEAM_JSON: JSON.stringify(config.team, null, 2),

    PROPERTIES_COUNT: String((config.properties ?? []).length),
    PROPERTIES_JSON: JSON.stringify(config.properties ?? [], null, 2),

    NEWS_COUNT: String((config.news ?? []).length),
    NEWS_JSON: JSON.stringify(config.news ?? [], null, 2),

    TECH_FRONTEND: config.tech.frontend,
    TECH_BACKEND: config.tech.backend,
    TECH_STORAGE: config.tech.storage,
    TECH_ANALYTICS: config.tech.analytics ? 'true' : 'false',
    TECH_AUTH: config.tech.auth,

    DEPLOY_WORKER_SUBDOMAIN: config.deploy.workerSubdomain,
    DEPLOY_PAGES_PROJECT_NAME: config.deploy.pagesProjectName,
    DEPLOY_CUSTOM_DOMAIN: config.deploy.customDomain || '',
    DEPLOY_CLOUDFLARE_ACCOUNT_ID: config.deploy.cloudflareAccountId || '',

    // Derived defaults
    KV_BINDING: 'CMS_DATA',
    ADMIN_USERNAME: config.brand.nameEn.replace(/\s+/g, ''),
    ADMIN_TOKEN: `${config.name}-admin-token`,
    FALLBACK_PASSWORD: 'admin360',
    DATA_KEY: 'cms_data',
    HISTORY_PREFIX: 'cms_history',

    DEFAULT_CMS_DATA: generateDefaultCMSData(config),
    CURRENT_YEAR: String(new Date().getFullYear()),

    // Aliases / backward-compatible variables used by project templates and modules
    BRAND_NAME: config.brand.nameCn || config.brand.nameEn || config.displayName,
    SITE_TITLE: config.displayName,

    HERO_HEADING: config.hero.titleLine1,
    HERO_SUBHEADING: config.hero.description,
    HERO_BACKGROUND_IMAGE: config.hero.backgroundValue || '',

    PAGES_PROJECT_NAME: config.deploy.pagesProjectName,
    YOUR_SUBDOMAIN: config.deploy.workerSubdomain,

    KV_BINDING_NAME: 'CMS_DATA',
    KV_NAMESPACE_BINDING: 'CMS_DATA',
    KV_NAMESPACE_ID: config.deploy.cloudflareAccountId || 'REPLACE_WITH_KV_NAMESPACE_ID',

    R2_BINDING: 'MEDIA_BUCKET',
    R2_BINDING_NAME: 'MEDIA_BUCKET',
    R2_BUCKET_NAME: `${config.name}-media`,
    R2_PUBLIC_URL: `https://pub-${config.deploy.cloudflareAccountId || 'example'}.r2.dev`,

    NODE_VERSION: '20',
    GITHUB_ACTIONS_BRANCH: 'main',
    GITHUB_ACTIONS_DEPLOY_BRANCH: 'main',

    ADMIN_FALLBACK_PASSWORD: 'admin360',
    CONTENT_BLOCKS_JSON: JSON.stringify(config.contentBlocks, null, 2),
    DOCTOR_NAME: config.team[0]?.name || 'Dr. Name',
    DOCTOR_TITLE: config.team[0]?.title || 'Director',
  };

  for (const [key, value] of Object.entries(config.customVars)) {
    flat[key] = value;
  }

  return flat;
}

export function assembleLayer(options: AssembleOptions): AssembleResult {
  const { layer, projectConfig, template, modules, archetype } = options;

  let effectiveTemplate = template;
  if (archetype) {
    effectiveTemplate = getArchetypeTemplate(archetype).layerTemplates[layer - 1];
  }
  if (effectiveTemplate === undefined) {
    throw new Error('assembleLayer: either template or a known archetype must be provided');
  }

  const { content: withModules, count: moduleCount } = embedModules(effectiveTemplate, modules);
  const variables = flattenConfig(projectConfig);
  const { content: replaced, count: variableCount } = replaceVariables(withModules, variables);
  const unreplacedVariables = findUnreplacedVariables(replaced);

  return {
    content: replaced,
    unreplacedVariables,
    moduleCount,
    variableCount,
  };
}

export { findUnreplacedVariables };
